import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CommonChip } from '../../lib/types';
import PetDetails from '../ChipDetails/PetDetails/PetDetails';
import OutlineButton from '../OutlineButton';
import ActionsContainer from '../ActionsContainer/ActionsContainer';
import ModalAlert from '../modals/ModalAlert';
import { useResolveChipTransfer } from '../../lib/mutations';
import { ResolveChipTransferInput, ChipTransferStatus } from '../../../types/gql-op-types';
import { gqlTypes } from '../../../types/gqlTypes';
import InfoCard from '../InfoCard/InfoCard';
import dogPlaceholderImage from '../../../assets/images/dog-placeholder.png';
import petDetailsStyles from '../ChipDetails/PetDetails/PetDetails.module.scss';
import sharedStyles from '../DetailsSharedStyles.module.scss';

export interface AcceptTransferProps {
  chip: CommonChip;
  pet: gqlTypes.basePet | undefined | null;
  transfer: gqlTypes.transfer;
  reload(): void;
  canEdit: boolean;
  isReseller: boolean;
}

const getRequesterName = (transfer: gqlTypes.transfer) => {
  const fullName = [transfer.contactInfo.firstName?.trim(), transfer.contactInfo.lastName?.trim()]
    .filter(Boolean)
    .join(' ');
  return fullName || transfer.contactInfo.primaryEmail || 'Unknown';
};

/**
 * When the transfer request doesn't include a pet, we show only contact details instead.
 * Email/phone/address rows are restricted to reseller users; non-resellers only see the requester's name.
 */
function TransferRequestContactDetails({
  chip,
  transfer,
  isReseller,
}: {
  chip: CommonChip;
  transfer: gqlTypes.transfer;
  isReseller: boolean;
}) {
  const requesterName = getRequesterName(transfer);

  const formatAddress = () => {
    const parts = [
      transfer.contactInfo.line1,
      transfer.contactInfo.line2,
      transfer.contactInfo.city,
      transfer.contactInfo.state,
      transfer.contactInfo.zipcode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'No data';
  };

  const Header = (
    <>
      <div className={petDetailsStyles.header}>
        <div className={petDetailsStyles.picture}>
          <div>
            <img src={dogPlaceholderImage} alt="Transfer Request" />
            <div className={`${petDetailsStyles.statusLabel} ${petDetailsStyles.pending}`}>Transfer Pending</div>
          </div>
        </div>
        <div className={petDetailsStyles.petName}>{requesterName}</div>
        <div className={petDetailsStyles.lastUpdated}>Transfer Request</div>
      </div>
      <div className={petDetailsStyles.chipId}>
        <div className={petDetailsStyles.chipIdLabel}>Chip ID</div>
        <div>{chip.serialNumber}</div>
      </div>
    </>
  );

  const rows = isReseller
    ? [
        [
          { label: 'Email', value: transfer.contactInfo.primaryEmail ?? 'No data' },
          { label: 'Phone', value: transfer.contactInfo.primaryPhone ?? 'No data' },
        ],
        [{ label: 'Address', value: formatAddress() }],
        [{ label: 'Pet', value: 'No pet details in request' }],
      ]
    : [[{ label: 'Pet', value: 'No pet details in request' }]];

  return (
    <div className={sharedStyles.main}>
      <InfoCard header={Header} rows={rows} />
    </div>
  );
}

export default function AcceptTransfer({ chip, pet, transfer, reload, canEdit, isReseller }: AcceptTransferProps) {
  const history = useHistory();
  const [{ complete, wasSuccess }, setResolutionState] = useState({
    complete: false,
    wasSuccess: false,
  });

  const [resolveChipTransfer] = useResolveChipTransfer();

  const doResolve = async (input: ResolveChipTransferInput) => {
    try {
      await resolveChipTransfer({
        variables: { input },
      });
      setResolutionState({ complete: true, wasSuccess: true });
      history.push('/manage');
    } catch (err) {
      console.error(err);
      setResolutionState({ complete: true, wasSuccess: false });
    }
  };

  const doAccept = () =>
    doResolve({
      chipTransferId: transfer.id,
      status: ChipTransferStatus.ACCEPTED,
    });

  const doReject = () =>
    doResolve({
      chipTransferId: transfer.id,
      status: ChipTransferStatus.REJECTED,
    });

  return (
    <div>
      {pet ? (
        <PetDetails
          chip={chip}
          pet={pet}
          status="pending"
          contactInfo={isReseller ? transfer.contactInfo : undefined}
          ownerNameOverride={isReseller ? undefined : getRequesterName(transfer)}
          onPetEdited={
            canEdit
              ? () => {
                  reload();
                }
              : undefined
          }
        />
      ) : (
        <TransferRequestContactDetails chip={chip} transfer={transfer} isReseller={isReseller} />
      )}
      <ActionsContainer>
        <OutlineButton onClick={doAccept}>Accept Transfer</OutlineButton>
        <div className="link link__underlined" onClick={doReject}>
          Reject transfer
        </div>
      </ActionsContainer>
      <ModalAlert
        message={wasSuccess ? `Transfer complete!` : `An unknown error occurred`}
        open={complete}
        onClose={() => reload()}
      />
    </div>
  );
}
